import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import categories from "../../utils/categories";
import logo from "../../assets/digikorestudios.png";
import { Pause, Play, ArrowLeft } from "lucide-react";

const SingleSongPage = () => {
  const { categoryName, subCategoryName, songIndex } = useParams();
  const navigate = useNavigate();

  const [category, setCategory] = useState(null);
  const [subCategory, setSubCategory] = useState(null);
  const [song, setSong] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const audioRef = useRef(new Audio());
  const isSeekingRef = useRef(false);

  // DevTools detection
  useEffect(() => {
    const threshold = 160;

    const isMobile = () =>
      /Android|iPhone|iPad|iPod|Opera Mini|IEMobile/i.test(
        navigator.userAgent,
      ) || window.innerWidth < 768;

    const redirectAway = () => {
      const audio = audioRef.current;
      audio.pause();
      audio.currentTime = 0;
      setIsPlaying(false);
      navigate("/jingle-collection", { replace: true });
    };

    const sizeGapOpen = () =>
      window.outerWidth - window.innerWidth > threshold ||
      window.outerHeight - window.innerHeight > threshold;

    const timingOpen = () => {
      const start = performance.now();
      debugger; // eslint-disable-line no-debugger
      const end = performance.now();
      return end - start > 50;
    };

    const detectDevTools = () => {
      if (isMobile()) return;
      if (sizeGapOpen() || timingOpen()) redirectAway();
    };

    detectDevTools();
    const t = setTimeout(detectDevTools, 300);

    window.addEventListener("resize", detectDevTools);
    const interval = setInterval(detectDevTools, 800);

    return () => {
      clearTimeout(t);
      clearInterval(interval);
      window.removeEventListener("resize", detectDevTools);
    };
  }, [navigate]);

  // Load category and song data
  useEffect(() => {
    const foundCategory = categories.find((cat) => cat.slug === categoryName);

    if (foundCategory) {
      setCategory(foundCategory);

      const foundSubCategory = foundCategory.songs.find(
        (sub) =>
          sub.subCategory.toLowerCase().replace(/ /g, "-") === subCategoryName,
      );

      if (foundSubCategory && foundSubCategory.songList[songIndex]) {
        setSubCategory(foundSubCategory);
        setSong(foundSubCategory.songList[songIndex]);
      }
    }
  }, [categoryName, subCategoryName, songIndex]);

  // Handle audio playback
  useEffect(() => {
    const audio = audioRef.current;

    if (song) {
      audio.src = song.url;
      audio.load();

      if (isPlaying) {
        audio.play().catch((err) => console.warn("Playback failed:", err));
      }
    }

    const updateProgress = () => {
      if (isSeekingRef.current) return;
      const dur = audio.duration || 0;
      const ct = audio.currentTime || 0;
      setDuration(dur);
      setCurrentTime(ct);
      if (dur > 0) setProgress((ct / dur) * 100);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime(0);
    };

    audio.addEventListener("timeupdate", updateProgress);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("loadedmetadata", () =>
      setDuration(audio.duration || 0),
    );

    return () => {
      audio.removeEventListener("timeupdate", updateProgress);
      audio.removeEventListener("ended", handleEnded);
      audio.pause();
    };
  }, [song]);

  const handlePlayPause = () => {
    const audio = audioRef.current;
    if (audio.paused) {
      audio
        .play()
        .then(() => setIsPlaying(true))
        .catch((err) => console.warn(err));
    } else {
      audio.pause();
      setIsPlaying(false);
    }
  };

  const handleSeek = (e) => {
    const value = Number(e.target.value);
    const audio = audioRef.current;
    if (audio && audio.duration) {
      const nextTime = (value / 100) * audio.duration;
      audio.currentTime = nextTime;
      setProgress(value);
      setCurrentTime(nextTime);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleBack = () => {
    audioRef.current.pause();
    navigate(`/jingle-collection/${categoryName}`);
  };

  if (!category || !subCategory || !song) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-orange-50 via-white to-amber-50">
      <div className="flex-1 container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <img
            src={logo}
            className="w-23 h-14 mx-auto mb-4"
            alt="Digikore Studios"
          />
          <h1 className="text-4xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
            {song.title}
          </h1>
          <p className="text-gray-600 mt-2">
            {category.name} / {subCategory.subCategory}
          </p>
        </div>

        {/* Back Button */}
        <button
          onClick={handleBack}
          className="mb-8 flex items-center gap-2 text-orange-600 hover:text-orange-700 transition-colors"
        >
          <ArrowLeft size={20} />
          Back to {category.name} Collection
        </button>

        {/* Main Player Card */}
        <div className="max-w-2xl mx-auto mb-4">
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl p-8 border border-white/20">
            {/* Album Art */}
            <div className="w-64 h-64 mx-auto mb-8 rounded-2xl overflow-hidden shadow-xl">
              <img
                src={category.image}
                alt={song.title}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Song Info */}
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-2">
                {song.title}
              </h2>
              <p className="text-gray-500">{subCategory.subCategory}</p>
              <p className="text-gray-400 text-sm mt-1">{category.name}</p>
            </div>

            {/* Progress Bar */}
            <div className="mb-6">
              <input
                type="range"
                min="0"
                max="100"
                step="0.1"
                value={progress}
                onChange={handleSeek}
                className="w-full h-2 rounded-full cursor-pointer accent-orange-500"
              />
              <div className="flex justify-between text-sm text-gray-500 mt-2">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            {/* Play/Pause Button */}
            <div className="text-center">
              <button
                onClick={handlePlayPause}
                className="w-20 h-20 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-xl hover:scale-105 transition-all duration-300 flex items-center justify-center mx-auto"
              >
                {isPlaying ? (
                  <Pause size={32} />
                ) : (
                  <Play size={32} className="ml-1" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="w-full bg-gray-100 text-center py-4 text-sm text-gray-600">
        <p>
          For contact and support:{" "}
          <a
            href="mailto:prapti@digikore.com"
            className="text-blue-600 hover:underline"
          >
            prapti@digikore.com
          </a>
        </p>
        <p className="mt-1">© 2026 Digikore Studios Limited</p>
      </footer>
    </div>
  );
};

export default SingleSongPage;
